import { mergeByReceivedAt } from "./forecastLists.js";

// Apply a manual per-user ordering to the Forecast Manager list.
//
// `orderIds` is the user's saved arrangement (an array of client ids, persisted
// in users.preferences.forecastOrder). Forecasts present in it render in that
// sequence; forecasts NOT in it (never positioned — e.g. a freshly created one)
// render FIRST, preserving their incoming order (the caller passes newest-first),
// so new forecasts appear at the top of their section until the user next drags
// (which re-captures the full order). Pure + stable; the input is not mutated.
//
// Applied to the whole forecasts array BEFORE the Forecast Manager's
// watchlist/recent section filters, so each section preserves this relative order.
//
// @param {Array<{id: string}>} forecasts  newest-first (as selectLegacyForecasts returns)
// @param {string[]} orderIds              the manual order (client ids); may reference
//                                         now-absent ids (ignored) and may be empty
// @returns {Array} a new array with the ordering applied
export function orderForecasts(forecasts, orderIds) {
  if (!Array.isArray(forecasts)) return [];
  if (!Array.isArray(orderIds) || orderIds.length === 0) return forecasts;
  // First occurrence of each id wins (dedupe a malformed order defensively).
  const idx = new Map();
  for (let i = 0; i < orderIds.length; i++) {
    const id = orderIds[i];
    if (!idx.has(id)) idx.set(id, i);
  }
  const unlisted = [];
  const listed = [];
  for (const f of forecasts) {
    if (f && idx.has(f.id)) listed.push(f);
    else unlisted.push(f);
  }
  listed.sort((a, b) => idx.get(a.id) - idx.get(b.id));
  return [...unlisted, ...listed];
}

/**
 * The Forecast Manager's displayed ids: owned and shared rows merged by when the viewer received
 * them, then put in the manual order. Drags order over this list, so a shared row keeps its
 * place when an owned card moves around it.
 *
 * @param {Array<{id: string}>} owned
 * @param {Array<{id: string}>} shared
 * @param {string[]} orderIds
 * @returns {string[]}
 */
export function boardOrderIds(owned, shared, orderIds) {
  return orderForecasts(mergeByReceivedAt(owned, shared), orderIds)
    .filter(Boolean)
    .map((f) => f.id);
}

/**
 * Place `id` (or a block of ids, kept in the given order) just before `beforeId` in a displayed
 * order, or just after `afterId` when there is no row after it (the end of a list); with
 * neither, it goes first. Ids not in the order are ignored as anchors. An anchor that is
 * itself being moved means the drop is next to where it already is: the order is unchanged.
 * Pure: returns a new array.
 *
 * @param {string[]} ids the displayed order
 * @param {string|string[]} id
 * @param {string|null} beforeId
 * @param {string|null} afterId
 * @returns {string[]}
 */
export function moveIdInOrder(ids, id, beforeId, afterId) {
  const moving = Array.isArray(id) ? id : [id];
  if (moving.includes(beforeId) || moving.includes(afterId)) return ids;
  const block = new Set(moving);
  const next = ids.filter((x) => !block.has(x));
  const before = beforeId ? next.indexOf(beforeId) : -1;
  if (before >= 0) {
    next.splice(before, 0, ...moving);
    return next;
  }
  const after = afterId ? next.indexOf(afterId) : -1;
  next.splice(after >= 0 ? after + 1 : 0, 0, ...moving);
  return next;
}

/**
 * `order` without `id` — for dropping a deleted forecast from the saved drag order. Returns the
 * SAME array when `id` is absent (or `order` is not an array), so callers can skip a save.
 *
 * @param {string[] | null | undefined} order the saved order
 * @param {string} id
 * @returns {string[] | null | undefined} `order` itself when there is nothing to remove
 */
export function withoutOrderId(order, id) {
  if (!Array.isArray(order) || !order.includes(id)) return order;
  return order.filter((x) => x !== id);
}
